import { NoddiBasicCard, NoddiSkeleton, useIsMobile } from 'noddi-ui-web';

import { SalesItemBookingCategoryType } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { useBookingAddress } from 'noddi-web';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { translateBookingCategories } from '../../../../utils/translateBookingCategory';
import { ApiErrorMessageWithTrans } from '../../../Error/ApiErrorMessageWithTrans';
import { bookingCategoryVariants } from './variants';

type ServiceCategoryHeaderProps = {
  priceFrom?: string;
  isExpanded: boolean;
  numberOfSelectedSalesItems: number;
  bookingCategoryType: SalesItemBookingCategoryType;
  isFleet?: boolean;
};

export const ServiceCategoryHeader = ({
  priceFrom,
  isExpanded,
  numberOfSelectedSalesItems,
  bookingCategoryType,
  isFleet
}: ServiceCategoryHeaderProps) => {
  const bookingAddress = useBookingAddress();
  const { currentBrand } = useCurrentBrand();

  const {
    data: bookingCategories,
    error: bookingCategoriesError,
    isPending: isBookingCategoriesPending
  } = noddiAsync.useGet({
    type: URLKeys.getBookingCategoriesForNewBooking,
    input: {
      addressId: bookingAddress?.id as number,
      brandDomain: currentBrand
    },
    queryConfig: { enabled: !!bookingAddress?.id }
  });

  const isMobile = useIsMobile();
  const serviceName = translateBookingCategories(bookingCategoryType);

  const variant = bookingCategoryVariants(isMobile)[bookingCategoryType];

  if (bookingCategoriesError) {
    return <ApiErrorMessageWithTrans error={bookingCategoriesError} />;
  }
  if (isBookingCategoriesPending) {
    return <NoddiSkeleton className='h-24 w-full' />;
  }

  const brand = bookingCategories.find((item) => item.type === bookingCategoryType)?.brand;
  return (
    <NoddiBasicCard
      className={cn(
        'relative flex w-full justify-between gap-2 overflow-hidden border-none bg-no-repeat shadow-none duration-300',
        isFleet && 'items-center'
      )}
    >
      <div className='flex gap-2'>
        <div className='flex size-8 shrink-0 items-center justify-center'>
          {numberOfSelectedSalesItems > 0 ? (
            <div className='flex size-8 items-center justify-center rounded-full bg-card-selected-background text-card-selected-foreground'>
              {numberOfSelectedSalesItems}
            </div>
          ) : (
            <div>
              <NoddiIcon size='medium' name={variant.iconName} />
            </div>
          )}
        </div>
        <div className='flex flex-col gap-1'>
          <span className='break-after-auto text-2xl hyphens-auto'>{serviceName}</span>
          {priceFrom && <span className='text-sm'>{priceFrom}</span>}
        </div>
      </div>
      <div className='flex flex-col items-end gap-2'>
        {!isFleet && (
          <div className={cn('transition-transform duration-300 ease-out', isExpanded && 'rotate-180')}>
            <NoddiIcon name='ChevronDown' />
          </div>
        )}

        {!!brand?.logo && (
          <div className='w-20 shrink-0'>
            <img src={brand.logo.url} alt={brand.name} />
          </div>
        )}
      </div>
    </NoddiBasicCard>
  );
};
